/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import useVehicleMqttConnectionStatus from '@ion/query/src/hooks/useVehicleMqttConnectionStatus';
import {getCleanSessionTooltip, isCleanSessionInProgress} from './CleanSessionUtils';

export interface UseCleanSessionGateResult {
  cleanSessionInProgress: boolean;
  cleanSessionTooltip: string;
}

/**
 * Tracks whether DM reports an active clean session for one device, so actions
 * that conflict with a clean session can be disabled with an explanation.
 *
 * - Re-evaluates whenever the ESN changes or the connection state refetches.
 * - `cleanSessionTooltip` is empty when no clean session is in progress.
 *
 * ```typescript
 * import useCleanSessionGate from
 *   '@ion/applications/src/pages/vehicle/manager/useCleanSessionGate';
 * ...
 *
 * const {cleanSessionInProgress, cleanSessionTooltip} = useCleanSessionGate(esn);
 * ```
 *
 * @param   esn The device ESN to check; an empty value never blocks.
 * @returns The clean session state and the tooltip explaining the block.
 */
const useCleanSessionGate = (esn: string): UseCleanSessionGateResult => {
  const esns                   = useMemo(() => (esn ? [esn] : []), [esn]);
  const {cleanSessionMap}      = useVehicleMqttConnectionStatus(esns);
  const cleanSessionInProgress = isCleanSessionInProgress(cleanSessionMap, esn);

  return {
    cleanSessionInProgress,
    cleanSessionTooltip : getCleanSessionTooltip(cleanSessionInProgress)
  };
};

export default useCleanSessionGate;
